import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Wand2, CheckCircle, AlertCircle, Download, Eye, Trash2, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CustomTemplateService } from '@/services/customTemplateService';

interface TemplateField {
  field: string;
  placeholder: string;
  context: string;
}

interface CustomTemplate {
  id: string;
  template_name: string;
  original_filename: string;
  template_file_url: string;
  field_mappings: TemplateField[];
  style_metadata: {
    hasStyles: boolean;
    hasTheme: boolean;
    hasNumbering: boolean;
    documentStructure: {
      paragraphCount: number;
      sections: number;
    };
    extractedAt: string;
  };
  created_at: string;
}

export default function CustomTemplateGenerator() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generatedTemplate, setGeneratedTemplate] = useState<CustomTemplate | null>(null);
  const [existingTemplates, setExistingTemplates] = useState<CustomTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadExistingTemplates();
  }, []);

  const loadExistingTemplates = async () => {
    try {
      setIsLoading(true);
      const templates = await CustomTemplateService.getUserTemplates();
      setExistingTemplates(templates);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: "Error Loading Templates",
        description: "Failed to load your existing templates",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      await CustomTemplateService.deleteTemplate(templateId);
      setExistingTemplates(prev => prev.filter(t => t.id !== templateId));
      toast({
        title: "Template Deleted",
        description: "Template has been successfully deleted"
      });
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete the template",
        variant: "destructive"
      });
    }
  };
  
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.docx')) {
        toast({
          title: "Invalid File Type",
          description: "Please select a DOCX file.",
          variant: "destructive"
        });
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast({
          title: "File Too Large",
          description: "Please select a file smaller than 10MB.",
          variant: "destructive"
        });
        return;
      }
      
      setSelectedFile(file);
      // Auto-generate template name from filename
      const baseName = file.name.replace('.docx', '').replace(/[^a-zA-Z0-9\s]/g, '');
      setTemplateName(baseName || 'Custom Template');
    }
  }, [toast]);

  const generateTemplate = async () => {
    if (!selectedFile || !templateName.trim()) {
      toast({
        title: "Missing Information",
        description: "Please select a file and enter a template name.",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    setProgress(0);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('templateName', templateName.trim());

      const { data, error } = await supabase.functions.invoke('generate-custom-docmosis-template', {
        body: formData
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (error) {
        throw new Error(error.message || 'Failed to generate template');
      }

      if (!data.success) {
        throw new Error(data.error || 'Template generation failed');
      }

      setGeneratedTemplate(data.template);
      // Refresh the templates list
      loadExistingTemplates();
      toast({
        title: "Template Generated Successfully",
        description: `Your custom template "${templateName}" is ready to use!`
      });

    } catch (error) {
      console.error('Error generating template:', error);
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : 'Failed to generate template',
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadTemplate = () => {
    if (generatedTemplate?.template_file_url) {
      window.open(generatedTemplate.template_file_url, '_blank');
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setTemplateName('');
    setGeneratedTemplate(null);
    setProgress(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent mb-4">
            Custom Template Generator
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Upload your DOCX resume to create a reusable Docmosis template that preserves your exact formatting and styling
          </p>
        </div>

        <Tabs defaultValue="create" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="create">Create Template</TabsTrigger>
            <TabsTrigger value="library">My Templates ({existingTemplates.length})</TabsTrigger>
          </TabsList>
          
          <TabsContent value="create" className="mt-6">
            {!generatedTemplate ? (
          <div className="space-y-6">
            {/* File Upload Section */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Upload Resume Document
                </CardTitle>
                <CardDescription>
                  Select a DOCX file to extract styling and create a custom template
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="file-upload">Choose DOCX File</Label>
                    <Input
                      id="file-upload"
                      type="file"
                      accept=".docx"
                      onChange={handleFileSelect}
                      className="mt-1"
                    />
                  </div>
                  
                  {selectedFile && (
                    <Alert>
                      <FileText className="w-4 h-4" />
                      <AlertDescription>
                        Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Template Configuration */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5" />
                  Template Configuration
                </CardTitle>
                <CardDescription>
                  Configure your custom template settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="template-name">Template Name</Label>
                    <Input
                      id="template-name"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="Enter a name for your template"
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Generation Progress */}
            {isGenerating && (
              <Card className="shadow-lg">
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Generating Template...</span>
                      <span className="text-sm text-muted-foreground">{progress}%</span>
                    </div>
                    <Progress value={progress} className="w-full" />
                    <p className="text-sm text-muted-foreground text-center">
                      Extracting document structure and creating placeholders...
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Generate Button */}
            <div className="flex justify-center">
              <Button
                onClick={generateTemplate}
                disabled={!selectedFile || !templateName.trim() || isGenerating}
                size="lg"
                className="px-8"
              >
                {isGenerating ? (
                  <>
                    <Wand2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Template...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate Template
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Success View */
          <div className="space-y-6">
            <Card className="shadow-lg border-green-200 bg-green-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-800">
                  <CheckCircle className="w-5 h-5" />
                  Template Generated Successfully!
                </CardTitle>
                <CardDescription>
                  Your custom template is ready to use for resume generation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold mb-3">Template Details</h3>
                    <div className="space-y-2 text-sm">
                      <div><strong>Name:</strong> {generatedTemplate.template_name}</div>
                      <div><strong>Original File:</strong> {generatedTemplate.original_filename}</div>
                      <div><strong>Created:</strong> {new Date(generatedTemplate.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-3">Style Information</h3>
                    <div className="flex flex-wrap gap-2">
                      {generatedTemplate.style_metadata.hasStyles && (
                        <Badge variant="secondary">Custom Styles</Badge>
                      )}
                      {generatedTemplate.style_metadata.hasTheme && (
                        <Badge variant="secondary">Theme Applied</Badge>
                      )}
                      {generatedTemplate.style_metadata.hasNumbering && (
                        <Badge variant="secondary">List Formatting</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      {generatedTemplate.style_metadata.documentStructure.paragraphCount} paragraphs, {' '}
                      {generatedTemplate.style_metadata.documentStructure.sections} sections
                    </p>
                  </div>
                </div>

                {generatedTemplate.field_mappings.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold mb-3">Detected Fields ({generatedTemplate.field_mappings.length})</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {generatedTemplate.field_mappings.slice(0, 6).map((field, index) => (
                        <Badge key={index} variant="outline" className="justify-start">
                          {field.field}
                        </Badge>
                      ))}
                      {generatedTemplate.field_mappings.length > 6 && (
                        <Badge variant="outline" className="justify-start">
                          +{generatedTemplate.field_mappings.length - 6} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-4 mt-6">
                  <Button onClick={downloadTemplate} variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                  <Button onClick={resetForm} variant="outline">
                    Create Another Template
                  </Button>
                  <Button onClick={() => navigate('/resume-builder')}>
                    <Eye className="w-4 h-4 mr-2" />
                    Use Template
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
          </TabsContent>
          
          <TabsContent value="library" className="mt-6">
            <div className="space-y-6">
              {isLoading ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center py-8">
                      <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-muted-foreground">Loading your templates...</p>
                    </div>
                  </CardContent>
                </Card>
              ) : existingTemplates.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No Templates Yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Create your first custom template to get started
                      </p>
                      <Button onClick={() => (document.querySelector('[data-value="create"]') as HTMLElement)?.click()}>
                        <Upload className="w-4 h-4 mr-2" />
                        Create Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-6">
                  {existingTemplates.map((template) => (
                    <Card key={template.id} className="shadow-lg">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="flex items-center gap-2">
                              <FileText className="w-5 h-5" />
                              {template.template_name}
                            </CardTitle>
                            <CardDescription>
                              Based on: {template.original_filename}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            {new Date(template.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                          <div>
                            <h4 className="font-medium mb-2">Template Features</h4>
                            <div className="flex flex-wrap gap-2">
                              {template.style_metadata.hasStyles && (
                                <Badge variant="secondary">Custom Styles</Badge>
                              )}
                              {template.style_metadata.hasTheme && (
                                <Badge variant="secondary">Theme Applied</Badge>
                              )}
                              {template.style_metadata.hasNumbering && (
                                <Badge variant="secondary">List Formatting</Badge>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">Structure</h4>
                            <p className="text-sm text-muted-foreground">
                              {template.style_metadata.documentStructure.paragraphCount} paragraphs, {' '}
                              {template.style_metadata.documentStructure.sections} sections
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {template.field_mappings.length} detected fields
                            </p>
                          </div>
                        </div>

                        {template.field_mappings.length > 0 && (
                          <div className="mb-6">
                            <h4 className="font-medium mb-2">Available Fields</h4>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                              {template.field_mappings.slice(0, 8).map((field, index) => (
                                <Badge key={index} variant="outline" className="justify-start text-xs">
                                  {field.field}
                                </Badge>
                              ))}
                              {template.field_mappings.length > 8 && (
                                <Badge variant="outline" className="justify-start text-xs">
                                  +{template.field_mappings.length - 8} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-3">
                          <Button 
                            onClick={() => window.open(template.template_file_url, '_blank')}
                            variant="outline"
                            size="sm"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                          <Button 
                            onClick={() => navigate('/resume-builder')}
                            size="sm"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Use Template
                          </Button>
                          <Button 
                            onClick={() => handleDeleteTemplate(template.id)}
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}